/**
 * @fileoverview Sécurité du compte agent.
 *
 * Regroupe les sessions, le changement de mot de passe et l'accès au parcours
 * MFA. Toutes les mutations sont représentées côté UI via des sheets.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataCard,
  DataTable,
  EmptyState,
  FormField,
  MetricCard,
  PageHeader,
  PasswordInput,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@gnpc/ui';
import { KeyRound, LockKeyhole, Monitor, Save, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AccountNav } from '../features/account/AccountNav.tsx';
import { useCurrentUser } from '../features/auth/CapabilityProvider.tsx';

interface AccountSession {
  id: string;
  device: string;
  location: string;
  ipAddress: string;
  lastActivity: string;
  current: boolean;
}

const ACCOUNT_SESSIONS: AccountSession[] = [
  {
    id: 'ses_current',
    device: 'Chrome · macOS',
    location: 'Abidjan, Côte d’Ivoire',
    ipAddress: '196.49.12.44',
    lastActivity: '14 juil. 2026, 08:15',
    current: true,
  },
  {
    id: 'ses_mobile',
    device: 'Safari · iPhone',
    location: 'Abidjan, Côte d’Ivoire',
    ipAddress: '196.49.12.61',
    lastActivity: '13 juil. 2026, 18:42',
    current: false,
  },
];

const sessionColumns: ColumnDef<AccountSession>[] = [
  { accessorKey: 'device', header: 'Appareil' },
  { accessorKey: 'location', header: 'Localisation' },
  { accessorKey: 'ipAddress', header: 'IP' },
  { accessorKey: 'lastActivity', header: 'Dernière activité' },
  {
    accessorKey: 'current',
    header: 'Session',
    cell: ({ row }) => (
      <Badge variant={row.original.current ? 'default' : 'outline'}>
        {row.original.current ? 'Active' : 'Autorisée'}
      </Badge>
    ),
  },
];

export function AccountSecurityPage(): JSX.Element {
  const { user } = useCurrentUser();
  const [sessionSheetOpen, setSessionSheetOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  /** Copie locale mutable du mock : la révocation retire la session de la liste. */
  const [sessions, setSessions] = useState<AccountSession[]>(ACCOUNT_SESSIONS);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId) ?? null,
    [selectedSessionId, sessions],
  );

  /** Déconnexion en masse des sessions autres que la session courante (C-36). */
  const disconnectOtherSessions = () => {
    setSessions((current) => current.filter((session) => session.current));
    toast.success('Autres sessions déconnectées', {
      description: 'Toutes les sessions sauf la session courante ont été révoquées.',
    });
  };

  const confirmRevokeSession = () => {
    if (!selectedSession) return;
    setSessions((current) => current.filter((session) => session.id !== selectedSession.id));
    toast.success('Session révoquée', {
      description: `${selectedSession.device} — l'appareil devra se reconnecter avec mot de passe et MFA.`,
    });
    setRevokeDialogOpen(false);
    setSessionSheetOpen(false);
  };

  return (
    <>
      <div className="grid gap-6">
        <PageHeader
          eyebrow="Compte agent"
          title="Sécurité"
          description="Contrôler les accès au compte, les sessions actives et les méthodes de vérification."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline">
                <Link to="/account/mfa">
                  <KeyRound className="size-4" />
                  Gérer le MFA
                </Link>
              </Button>
              <Sheet>
                <SheetTrigger asChild>
                  <Button>
                    <LockKeyhole className="size-4" />
                    Changer le mot de passe
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
                  <SheetHeader className="border-border border-b p-5 pr-12">
                    <SheetTitle>Changer le mot de passe</SheetTitle>
                    <SheetDescription>
                      Le MFA reste demandé après validation du nouveau mot de passe.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="grid gap-4 p-5">
                    <FormField label="Mot de passe actuel" required>
                      <PasswordInput autoComplete="current-password" />
                    </FormField>
                    <FormField label="Nouveau mot de passe" required hint="12 caractères minimum.">
                      <PasswordInput autoComplete="new-password" minLength={12} />
                    </FormField>
                    <FormField label="Confirmer le nouveau mot de passe" required>
                      <PasswordInput autoComplete="new-password" minLength={12} />
                    </FormField>
                  </div>
                  <SheetFooter className="border-border border-t p-5">
                    <SheetClose asChild>
                      <Button
                        onClick={() =>
                          toast.success('Mot de passe modifié', {
                            description: 'Le code MFA reste demandé à la prochaine connexion.',
                          })
                        }
                      >
                        <Save className="size-4" />
                        Enregistrer
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button variant="outline">Annuler</Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          }
        />

        <AccountNav />

        <section className="grid gap-4 md:grid-cols-3">
          <MetricCard
            label="Mot de passe"
            value="À jour"
            description="Dernière modification il y a 18 jours."
            tone="success"
            icon={<LockKeyhole />}
          />
          <MetricCard
            label="MFA"
            value="Actif"
            description="Application TOTP vérifiée pour ce compte."
            tone="success"
            icon={<ShieldCheck />}
          />
          <MetricCard
            label="Sessions"
            value={sessions.length}
            description={`${user.fullName} a ${sessions.length} session(s) autorisée(s).`}
            icon={<Monitor />}
          />
        </section>

        <DataTable
          columns={sessionColumns}
          data={sessions}
          rowAction={{
            label: (session) => `Ouvrir le détail de la session ${session.device}`,
            onSelect: (session) => {
              setSelectedSessionId(session.id);
              setSessionSheetOpen(true);
            },
          }}
          mobileCardLabel="Sessions du compte"
          mobileCard={(session) => (
            <DataCard
              eyebrow={session.location}
              title={session.device}
              description={session.ipAddress}
              aside={
                <Badge variant={session.current ? 'default' : 'outline'}>
                  {session.current ? 'Active' : 'Autorisée'}
                </Badge>
              }
              meta={<span className="text-muted-foreground">{session.lastActivity}</span>}
            />
          )}
          emptyState={<EmptyState title="Aucune session" description="Les sessions autorisées apparaîtront ici." />}
          pagination={{ label: `${sessions.length} session(s)`, hasNextPage: false }}
        />

        {/* Politique de session + restriction d'accès par IP (C-36). */}
        <section className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Politique de session</CardTitle>
              <CardDescription>Expiration et déconnexion des sessions actives.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Expiration après inactivité</span>
                <span className="text-quipux-navy-950 font-medium">30 minutes</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Durée maximale de session</span>
                <span className="text-quipux-navy-950 font-medium">8 heures</span>
              </div>
              <Button
                variant="outline"
                className="justify-self-start"
                disabled={sessions.filter((session) => !session.current).length === 0}
                onClick={disconnectOtherSessions}
              >
                Déconnecter les autres sessions
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Restriction d'accès (IP)</CardTitle>
              <CardDescription>
                L'accès à la console financière est limité aux adresses autorisées.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2 text-sm">
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <span className="text-quipux-navy-950 font-mono text-xs">196.49.12.0/24</span>
                <span className="text-muted-foreground text-xs">Siège Quipux — Abidjan</span>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <span className="text-quipux-navy-950 font-mono text-xs">10.8.0.0/16</span>
                <span className="text-muted-foreground text-xs">VPN Quipux</span>
              </div>
              <p className="text-muted-foreground text-xs">
                Toute connexion hors de ces plages est refusée pour les rôles financiers.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>

      <Sheet open={sessionSheetOpen} onOpenChange={setSessionSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Détail de session</SheetTitle>
            <SheetDescription>{selectedSession?.device ?? 'Session du compte'}</SheetDescription>
          </SheetHeader>
          {selectedSession ? (
            <dl className="grid gap-4 p-5 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Appareil</dt>
                <dd className="text-quipux-navy-950 mt-1 font-medium">{selectedSession.device}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Localisation</dt>
                <dd className="text-quipux-navy-950 mt-1 font-medium">{selectedSession.location}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Adresse IP</dt>
                <dd className="text-quipux-navy-950 mt-1 font-medium">{selectedSession.ipAddress}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Dernière activité</dt>
                <dd className="text-quipux-navy-950 mt-1 font-medium">{selectedSession.lastActivity}</dd>
              </div>
            </dl>
          ) : null}
          <SheetFooter className="border-border border-t p-5">
            <Button
              variant="destructive"
              disabled={!selectedSession || selectedSession.current}
              onClick={() => setRevokeDialogOpen(true)}
            >
              Révoquer la session
            </Button>
            <SheetClose asChild>
              <Button variant="outline">Fermer</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Révoquer la session {selectedSession?.device ?? ''} ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L'appareil sera immédiatement déconnecté et devra se réauthentifier avec mot de
              passe et MFA. Cette action sera journalisée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRevokeSession}>Révoquer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
